import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Building, Cpu, Infinity as InfinityIcon, Calendar, Award, Target, ChevronLeft, ChevronRight } from 'lucide-react';
import ParticleBackground from '../components/ParticleBackground';

export default function AboutPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [currentSection, setCurrentSection] = useState<string>('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const galleryImages = [
    '/1858-135259 copy.jpg',
    '/1855-135303 copy.jpg',
    '/1853-094639 copy.jpg',
    '/1850-223109 copy.jpg'
  ];

  const nextImage = () => {
    setCurrentImageIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };

  useEffect(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(location.search);
    const sectionParam = urlParams.get('section');
    
    if (sectionParam) {
      setCurrentSection(sectionParam);
    } else {
      // Fallback to referrer detection
      const referrer = document.referrer;
      if (referrer.includes('/robotics')) {
        setCurrentSection('robotics');
      } else if (referrer.includes('/unlimited')) {
        setCurrentSection('unlimited');
      } else if (referrer.includes('/sex-machines')) {
        setCurrentSection('sex-machines');
      } else {
        // Default to unlimited if no specific section
        setCurrentSection('unlimited');
      }
    }
  }, [location.search]);

  const renderRoboticsAbout = () => (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Cpu className="w-12 h-12 text-blue-500" />
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 font-orbitron">
            {t('about.robotics.title')}
          </h2>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {t('about.robotics.description')}
          </p>

          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            {t('about.robotics.description2')}
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Target className="w-10 h-10 text-blue-500" />
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.robotics.specialization')}
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.cncPortals')}
            </p>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.cncRouters')}
            </p>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.customMachines')}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Award className="w-10 h-10 text-blue-500" />
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.robotics.results')}
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.soldPlotters')}
            </p>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.plasmaPlotters')}
            </p>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.experience')}{' '}
              <a
                href="https://www.youtube.com/user/CYBERTRONICRENO"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                YouTube kanálu
              </a>
            </p>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">•</span>
            </div>
            <p className="text-gray-300">
              {t('about.robotics.support')}
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-black/40 backdrop-blur-md border border-green-500/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
            <span className="text-2xl">🌱</span>
          </div>
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.robotics.responsibility')}
          </h3>
        </div>

        <div className="space-y-3">
          <p className="text-gray-300 text-lg">
            {t('about.robotics.wasteManagement')}
          </p>
          <p className="text-gray-300 text-lg font-semibold text-green-400">
            {t('about.robotics.solarEnergy')}
          </p>
        </div>
      </motion.div>
    </div>
  );

  const renderUnlimitedAbout = () => (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <InfinityIcon className="w-12 h-12 text-green-500" />
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-green-600 to-green-700 font-orbitron">
            {t('about.unlimited.title')}
          </h2>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {t('about.unlimited.description')}
          </p>

          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            {t('about.unlimited.description2')}
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Calendar className="w-10 h-10 text-green-500" />
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.unlimited.history')}
          </h3>
        </div>

        <div className="space-y-6">
          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">2001</span>
            </div>
            <div>
              <p className="text-gray-300">
                {t('about.unlimited.year2001')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">2005</span>
            </div>
            <div>
              <p className="text-gray-300">
                {t('about.unlimited.year2005')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-16 h-16 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-green-400 font-bold">2010</span>
            </div>
            <div>
              <p className="text-gray-300">
                {t('about.unlimited.year2010')}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Target className="w-10 h-10 text-green-500" />
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.unlimited.customDevelopment')}
          </h3>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <p className="text-gray-300 text-lg leading-relaxed">
              {t('about.unlimited.customDevelopmentDesc')}
            </p>

            <div className="bg-gray-800/50 rounded-lg p-4 border-l-4 border-green-500">
              <p className="text-gray-300 italic">
                {t('about.unlimited.qualityQuote')}
              </p>
            </div>
          </div>

          <div className="rounded-lg overflow-hidden border border-green-500/20 max-w-md mx-auto relative">
            <div className="relative w-full aspect-video">
              <img
                src={galleryImages[currentImageIndex]}
                alt={`Výroba elektroniky v CYBERTRONIC Unlimited - obrázek ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />

              {/* Previous button */}
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Next button */}
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {/* Image indicators */}
              <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      index === currentImageIndex ? 'bg-green-500' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="bg-gray-800/80 p-3">
              <p className="text-gray-400 text-sm text-center">
                Pohled do naší výroby ({currentImageIndex + 1}/{galleryImages.length})
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <Building className="w-10 h-10 text-green-500" />
          <h3 className="text-2xl font-bold text-white font-orbitron">
            {t('about.unlimited.divisions')}
          </h3>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-800/50 rounded-lg p-6">
            <h4 className="text-xl font-bold text-green-400 mb-3">
              {t('about.unlimited.cai')}
            </h4>
            <p className="text-gray-300">
              {t('about.unlimited.caiDesc')}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6">
            <h4 className="text-xl font-bold text-green-400 mb-3">
              {t('about.unlimited.cei')}
            </h4>
            <p className="text-gray-300">
              {t('about.unlimited.ceiDesc')}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6">
            <h4 className="text-xl font-bold text-green-400 mb-3">
              {t('about.unlimited.cbi')}
            </h4>
            <p className="text-gray-300">
              {t('about.unlimited.cbiDesc')}
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-6">
            <h4 className="text-xl font-bold text-green-400 mb-3">
              {t('about.unlimited.cdi')}
            </h4>
            <p className="text-gray-300">
              {t('about.unlimited.cdiDesc')}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const renderSexMachinesAbout = () => (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-8"
      >
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-12 h-12 bg-pink-500/20 rounded-lg flex items-center justify-center">
            <span className="text-2xl">💕</span>
          </div>
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-pink-600 to-pink-700 font-orbitron">
            {t('about.unlimited.title')}
          </h2>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 text-lg leading-relaxed mb-6">
            {t('about.unlimited.description')}
          </p>

          <p className="text-gray-300 text-lg leading-relaxed mb-8">
            {t('about.unlimited.description2')}
          </p>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20">
      <ParticleBackground />
      <section className="py-20 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            {currentSection === 'robotics' ? (
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 font-orbitron mb-6">
                Cybertronic Robotics
              </h1>
            ) : currentSection === 'sex-machines' ? (
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-pink-600 to-pink-700 font-orbitron mb-6">
                Ostatní
              </h1>
            ) : (
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 via-green-600 to-green-700 font-orbitron mb-6">
                Cybertronic Unlimited
              </h1>
            )}
          </motion.div>

          {currentSection === 'robotics' ? renderRoboticsAbout() : 
           currentSection === 'sex-machines' ? renderSexMachinesAbout() : 
           renderUnlimitedAbout()}
        </div>
      </section>
    </div>
  );
}